import {user_state, issue_json_get, program_state, v1api } from "../program-state"
import {z} from "zod"

// ===== brand =====
export const brand_schema = z.object({
    id: z.number(),
    name: z.string(),
    url: z.string(),
    logo: z.string().nullish(),
    meta_page: z.number().nullish(),
    ad_account_id: z.number().nullish(),
    meta_pixel_id: z.number().nullish(),
    instagram_asset_id: z.number().nullish(),
    linkedin_org: z.number().nullish(),
    linkedin_ad_account: z.number().nullish(),
    tiktok_user_id: z.number().nullish(),
    tiktok_identity_id: z.number().nullish(),
    tiktok_ad_account_id: z.number().nullish(),
    tiktok_pixel_id: z.number().nullish(),
    x_user_id: z.number().nullish(),
    x_ad_account_id: z.number().nullish(),
    x_pixel_id: z.number().nullish(),
})

export const brands_schema = z.array(brand_schema)
export type brand_t = z.infer<typeof brand_schema>

export const fetch_associated_brands = async (ps : program_state, us : user_state) => {
    const token = us.token
    const brands_task = issue_json_get(v1api`brands`, brands_schema, token)

    const results = await Promise.all([brands_task])

    const [b] = results

    const [bok, bresp] = b
    if (bok) {
        return bresp
    } else {
        console.log("failed: ", bresp)
    }
}
// ====== end brand ===========

// ==== facebook ===
type fbid = string & { readonly __tag: unique symbol };
export const z_fbid = z.string as unknown as () => z.ZodType<fbid, z.ZodTypeDef, fbid>

export const facebook_user_schema = z.object({
    id : z.number(),
    facebook_id : z.string(),
    name : z.string().nullish(),
    username : z.string().nullish()
})

export const facebook_users_schema = z.array(facebook_user_schema)

export type facebook_user_t = z.infer<typeof facebook_user_schema>

export const facebook_page_schema = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_pages_schema = z.array(facebook_page_schema)
export type facebook_page_t = z.infer<typeof facebook_page_schema>

export const facebook_ad_account_schema = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_ad_accounts_schema = z.array(facebook_ad_account_schema)
export type facebook_ad_account_t = z.infer<typeof facebook_ad_account_schema>

export const facebook_business_manager_schema = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_business_managers_parse = z.array(facebook_business_manager_schema)
export type facebook_business_manager_t = z.infer<typeof facebook_business_manager_schema>

export const facebook_instagram_schema = z.object({
    facebook_id : z_fbid(),
    username : z.string(),
    id : z.number(),
})
export const facebook_instagrams_schema = z.array(facebook_instagram_schema)
export type facebook_instagram_t = z.infer<typeof facebook_instagram_schema>

export const facebook_pixel_schema = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_pixels_schema = z.array(facebook_pixel_schema)
export type facebook_pixel_t = z.infer<typeof facebook_pixel_schema>

export type facebook_assets_t = {
    pages : facebook_page_t[],
    ad_accounts : facebook_ad_account_t[],
    business_managers : facebook_business_manager_t[],
    instagram_accounts : facebook_instagram_t[],
    pixels : facebook_pixel_t[],
}
// ======= end facebook ==========

// ====== linkedin ===============
const linkedin_user_schema = z.object({
    id : z.number(),
    linkedin_id : z.string(),
    first_name : z.string().nullish(),
    last_name : z.string().nullish(),
    username : z.string().nullish()
})

const linkedin_users_schema = z.array(linkedin_user_schema)

export type linkedin_user_t = z.infer<typeof linkedin_user_schema>

export const linkedin_organization_schema = z.object({
    username: z.string(),
    name : z.string(),
    id : z.number(),
})
export type linkedin_organization_t = z.infer<typeof linkedin_organization_schema>
export const linkedin_organizations_schema = z.array(linkedin_organization_schema)

export const linkedin_ad_account_schema = z.object({
    id : z.number(),
    name : z.string(),
    status: z.string(),
})
export type linkedin_ad_account_t = z.infer<typeof linkedin_ad_account_schema>
export const linkedin_ad_accounts_schema = z.array(linkedin_ad_account_schema)


export type linkedin_assets_t = {
    organizations : linkedin_organization_t[],
    ad_accounts : linkedin_ad_account_t[]
}

const monetary_schema = z.object({
    currencyCode: z.string(),
    amount: z.string()
});

export const linkedin_bid_suggestion_schema = z.object({
    bidLimits: z.object({
        max: monetary_schema,
        min: monetary_schema
    }),
    suggestedBid: z.object({
        max: monetary_schema,
        default: monetary_schema,
        min: monetary_schema
    }),
    dailyBudgetLimits: z.object({
        max: monetary_schema,
        default: monetary_schema,
        min: monetary_schema
    })
});

export type linkedin_bid_suggestion_t = z.infer<typeof linkedin_bid_suggestion_schema>
// ====== end linkedin ===========


// ====== tiktok ===========
const tiktok_user_schema = z.object({
    id : z.number(),
    tiktok_id : z.string(),
    displayname : z.string().nullish(),
    email : z.string().nullish()
})

const tiktok_users_schema = z.array(tiktok_user_schema)

export type tiktok_user_t = z.infer<typeof tiktok_user_schema>

const tiktok_business_center_schema = z.object({
    id: z.number(),
    bc_id : z.string(),
    company: z.string(),
    name : z.string()
})
export type tiktok_business_center_t = z.infer<typeof tiktok_business_center_schema>
const tiktok_business_centers_schema = z.array(tiktok_business_center_schema)


const tiktok_ad_account_schema = z.object({
    id: z.number(),
    name : z.string(),
    advertiser_id: z.string(),
    status : z.string(),
})
export type tiktok_ad_account_t = z.infer<typeof tiktok_ad_account_schema>
const tiktok_ad_accounts_schema = z.array(tiktok_ad_account_schema)


const tiktok_identity_schema = z.object({
    id: z.number(),
    identity_id : z.string(),
    identity_type : z.string(),
    display_name: z.string(),
})
export type tiktok_identity_t = z.infer<typeof tiktok_identity_schema>
const tiktok_identities_schema = z.array(tiktok_identity_schema)


const tiktok_pixel_schema = z.object({
    id: z.number(),
    pixel_id : z.string(),
    pixel_code : z.string(),
    pixel_name: z.string(),
})
export type tiktok_pixel_t = z.infer<typeof tiktok_pixel_schema>
const tiktok_pixels_schema = z.array(tiktok_pixel_schema)


export type tiktok_assets_t = {
    business_centers : tiktok_business_center_t[],
    ad_accounts : tiktok_ad_account_t[],
    identities: tiktok_identity_t[],
    pixels: tiktok_pixel_t[]
}
// ====== end tiktok ======

// ====== x (twitter) ===========
const x_user_schema = z.object({
    id : z.number(),
    x_id : z.string(),
    screen_name : z.string(),
    display_name : z.string(),
    email : z.string().nullish(),
    profile_image_url : z.string().nullish(),
    _owner_id : z.number(),
    _deleted : z.boolean()
})

const x_users_schema = z.array(x_user_schema)

export type x_user_t = z.infer<typeof x_user_schema>

const x_ad_account_schema = z.object({
    id: z.number(),
    x_id : z.string(),
    name : z.string(),
    timezone : z.string().nullish(),
    currency : z.string().nullish(),
    approval_status : z.string().nullish(),
    _x_user_id : z.number().optional(),
    _owner_id : z.number().optional(),
    _deleted : z.boolean().nullish()
})
export type x_ad_account_t = z.infer<typeof x_ad_account_schema>
export const x_ad_accounts_schema = z.array(x_ad_account_schema)

const x_pixel_schema = z.object({
    id: z.number(),
    x_id : z.string(),
    name : z.string(),
    status : z.string().nullish(),
    click_window : z.number().nullish(),
    view_through_window : z.number().nullish(),
    _x_ad_account_id : z.number().optional(),
    _owner_id : z.number().optional(),
    _deleted : z.boolean().nullish()
})
export type x_pixel_t = z.infer<typeof x_pixel_schema>
export const x_pixels_schema = z.array(x_pixel_schema)

const x_tweet_schema = z.object({
    id : z.string(),
    text : z.string(),
    created_at : z.string(),
    author_id : z.string(),
    public_metrics : z.object({
        retweet_count : z.number(),
        reply_count : z.number(),
        like_count : z.number(),
        impression_count : z.number().optional()
    })
})
export type x_tweet_t = z.infer<typeof x_tweet_schema>
const x_tweets_schema = z.array(x_tweet_schema)

export type x_assets_t = {
    ad_accounts : x_ad_account_t[],
    pixels: x_pixel_t[]
}
// ====== end x ======

// ====== user ============
const eres_user_schema = z.object({
    id: z.number(),
    email: z.string()
})

export type eres_user_schema_t = z.infer<typeof eres_user_schema>
// ======= end user =======

// ======= audience =========
export const audience_schema = z.object({
    id: z.number(),
    name : z.string(),
    brand_id : z.number(),
    gender: z.string(),
    age_from: z.number(),
    age_to: z.number(),
    linkedin__locations: z.nullable(z.array(z.string())),
    linkedin__interests: z.nullable(z.array(z.string())),
    linkedin__seniority: z.nullable(z.array(z.string())),
    linkedin__staff_count: z.nullable(z.string()),
    linkedin__job_function: z.nullable(z.array(z.string())),
    facebook__electoral_district: z.nullable(z.string()),
    facebook__locations: z.nullable(z.array(z.string())),
    facebook__interests: z.nullable(z.array(z.string())),
    facenook__life_events: z.nullable(z.array(z.string())),
    facebook__behaviors: z.nullable(z.array(z.string())),
    tiktok__locations: z.nullable(z.array(z.string())),
    tiktok__interests: z.nullable(z.array(z.string())),
    tiktok__action_categories: z.nullable(z.array(z.string())),
    x__locations: z.array(z.string()).nullish(),
    x__interests: z.array(z.string()).nullish(),
    x__keywords: z.array(z.string()).nullish(),
    interests: z.nullable(z.array(z.string())),
    estimated_facebook_reach: z.nullable(z.unknown()),
    estimated_linkedin_reach: z.nullable(z.unknown()),
    estimated_tiktok_reach: z.nullable(z.unknown()),
    estimated_x_reach: z.nullable(z.unknown())
})
export type audience_t = z.infer<typeof audience_schema>
export const audiences_schema = z.array(audience_schema)

// custom audience
const Facebook_audience_data_source_schema = z.object({
    type: z.string(),
    sub_type: z.string(),
    creation_params: z.string()
});

const Facebook_audience_delivery_status_schema = z.object({
    code: z.number(),
    description: z.string()
});

const Facebook_audience_operation_status_schema = z.object({
    code: z.number(),
    description: z.string()
});

const Facebook_audience_permission_for_actions_schema = z.object({
    can_edit: z.boolean(),
    can_see_insight: z.boolean(),
    can_share: z.boolean(),
    subtype_supports_lookalike: z.boolean(),
    supports_recipient_lookalike: z.boolean()
});

const Facebook_audience_extra_data_schema = z.object({
    name: z.string(),
    account_id: z.string(),
    approximate_count_lower_bound: z.number(),
    approximate_count_upper_bound: z.number(),
    customer_file_source: z.string(),
    data_source: Facebook_audience_data_source_schema,
    delivery_status: Facebook_audience_delivery_status_schema,
    description: z.string(),
    is_value_based: z.boolean(),
    lookalike_audience_ids: z.array(z.string()).nullish(),
    operation_status: Facebook_audience_operation_status_schema,
    permission_for_actions: Facebook_audience_permission_for_actions_schema,
    retention_days: z.number(),
    subtype: z.string(),
    time_content_updated: z.number(),
    time_created: z.number(),
    time_updated: z.number(),
    id: z.string()
});

const Facebook_custom_audience_schema = z.object({
    _ad_account_id: z.number(),
    delivery_status: Facebook_audience_delivery_status_schema,
    extra_data: Facebook_audience_extra_data_schema.nullable(),
    facebook_id: z.string(),
    description: z.string(),
    _created: z.string(),
    name: z.string(),
    is_value_based: z.boolean(),
    _deleted: z.nullable(z.unknown()),
    account_id: z.string(),
    operation_status: Facebook_audience_operation_status_schema,
    _updated: z.string().nullable(),
    approximate_count_lower_bound: z.number(),
    retention_days: z.number(),
    _creator: z.nullable(z.unknown()),
    _owner_id: z.number(),
    approximate_count_upper_bound: z.number(),
    time_content_updated: z.number(),
    id: z.number(),
    customer_file_source: z.string(),
    time_created: z.number(),
    _facebook_user_id: z.nullable(z.unknown()),
    data_source: Facebook_audience_data_source_schema,
    time_updated: z.number()
});

const Linkedin_audience_destination_schema = z.object({
    audienceSize: z.number(),
    created: z.number(),
    destination: z.string(),
    destinationSegmentId: z.string(),
    lastModified: z.number(),
    matchedCount: z.number(),
    status: z.string()
});

const Linkedin_custom_audience_schema = z.object({
    id: z.number(),
    name: z.string(),
    created: z.string(),
    input_count: z.number(),
    source_platform: z.string(),
    destinations: z.array(Linkedin_audience_destination_schema).nullable(),
    _created: z.string(),
    _updated: z.string().nullable(),
    account: z.string(),
    _owner_id: z.number(),
    _ad_account_id: z.number(),
    linkedin_id: z.string(),
    last_modified: z.string().nullable(),
    type: z.string(),
    extra_data: z.unknown().nullable(),
    _deleted: z.string().nullable(),
    _creator: z.unknown().nullable()
});

const Tiktok_audience_details_schema = z.object({
    audience_id: z.string(),
    is_expiring: z.boolean(),
    is_auto_refresh: z.boolean().nullish(),
    is_creator: z.boolean(),
    expired_time: z.string(),
    is_valid: z.boolean(),
    shared: z.boolean(),
    create_time: z.string(),
    msg: z.string().nullish(),
    error_msg: z.string().nullish(),
    type: z.string().nullish(),
    audience_sub_type: z.string().nullish(),
    calculate_type: z.string().nullable(),
    cover_num: z.number(),
    name: z.string(),
    rule: z.string().nullish(),
    owner_id: z.string().nullish(),
    lookalike_spec: z.unknown().nullable()
});

const TikTok_audience_schema = z.object({
    is_creator: z.boolean(),
    type: z.string().nullish(),
    extra_data: z.object({
      audience_history: z.array(z.object({
        msg: z.string().nullable(),
        action_detail: z.string(),
        editor: z.string(),
        opt_time: z.string(),
        action: z.string()
      })).nullish(),
      audience_details: Tiktok_audience_details_schema
    }).nullable(),
    expired_time: z.string(),
    audience_sub_type: z.string().nullish(),
    _created: z.string(),
    _owner_id: z.number(),
    is_valid: z.boolean(),
    calculate_type: z.string().nullable(),
    _deleted: z.string().nullable(),
    _ad_account_id: z.number(),
    shared: z.boolean(),
    cover_num: z.number(),
    name: z.string(),
    create_time: z.string(),
    owner_id: z.string().nullish(),
    _updated: z.string().nullable(),
    audience_id: z.string(),
    msg: z.string().nullable(),
    lookalike_spec: z.unknown().nullable(),
    _creator: z.unknown().nullable(),
    is_expiring: z.boolean(),
    error_msg: z.string().nullable(),
    rule: z.string().nullish(),
    id: z.number(),
    is_auto_refresh: z.boolean().nullish(),
    audience_history: z.array(z.object({
      msg: z.string().nullable(),
      action_detail: z.string(),
      editor: z.string(),
      opt_time: z.string(),
      action: z.string()
    })).nullish()
});

const custom_audience_schema = z.object({
    brand_id: z.number().nullable(),  // null because sync audience doesnt has any brand
    id: z.number(),
    status: z.string().nullable(),
    meta__facebook_audience: z.number().nullable(),
    meta__linkedin_audience: z.number().nullable(),
    meta__tiktok_audience: z.number().nullable(),
    _owner_id: z.number(),
    _created: z.string(),
    _updated: z.string().nullable(),
    _deleted: z.string().nullable(),
    _creator: z.unknown().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    file_id: z.number().nullable(),
    meta__facebook_message: z.string().nullable(),
    meta__linkedin_message: z.string().nullable(),
    meta__tiktok_message: z.string().nullable(),
    meta__x_message: z.string().nullish(),
    facebook_audience: Facebook_custom_audience_schema.nullish(),
    linkedin_audience: Linkedin_custom_audience_schema.nullish(),
    tiktok_audience: TikTok_audience_schema.nullish(),
    x_audience: z.unknown().nullish()
});

export type custom_audience_t = z.infer<typeof custom_audience_schema>
export const custom_audiences_schema = z.array(custom_audience_schema)
// ======= end audience ======


// ====== creative ===========
export const tiktok_config_schema = z.object({
    id: z.number(),
    budget_mode: z.nullable(z.string()),
    billing_event: z.nullable(z.string()),
    instance_ad_status: z.string().nullish(),
    budget: z.number(),
    bid_type: z.nullable(z.string()),
    instance_ad_message: z.nullable(z.object({
      title: z.nullable(z.string()),
      message: z.nullable(z.string()),
    })),
    _owner_id: z.number(),
    bid_amount: z.nullable(z.number()),
    optimize_goal: z.nullable(z.string()),
    _smart_creative_id: z.number(),
    schedule_type: z.nullable(z.string()),
    frequency: z.nullable(z.string()),
    instance_ad_id: z.nullable(z.number()),
    tiktok_user_id: z.nullable(z.number()),
    schedule_start_time: z.nullable(z.string()),
    frequency_schedule: z.nullable(z.string()),
    _created: z.string(),
    ad_text: z.nullable(z.string()),
    schedule_end_time: z.nullable(z.string()),
    video_id: z.number().nullish(),
    _deleted: z.nullable(z.string()),
    display_name: z.nullable(z.string()),
    external_type: z.nullable(z.string()),
    pixel_id: z.nullable(z.number()),
    _updated: z.nullable(z.string()),
    landing_page_url: z.nullable(z.string()),
    pacing: z.nullable(z.string()),
    optimization_event: z.nullable(z.string()),
    _creator: z.nullable(z.string()),
  });

const facebook_config_schema = z.object({
    tracking_parameters: z.nullable(z.string()),
    day_in_week: z.nullable(z.array(z.number())),
    _deleted: z.nullable(z.string()),
    start_time: z.nullable(z.string()),
    start_minute: z.nullable(z.string()),
    _updated: z.nullable(z.string()),
    end_time: z.nullable(z.string()),
    end_minute: z.nullable(z.string()),
    _creator: z.nullable(z.string()),
    billing_event: z.nullable(z.string()),
    pixel_id: z.nullable(z.string()),
    _owner_id: z.number(),
    optimization_goal: z.nullable(z.string()),
    budget: z.nullable(z.number()),
    id: z.number(),
    instance_ad_status: z.nullable(z.string()),
    bid_amount: z.nullable(z.number()),
    _smart_creative_id: z.number(),
    instance_ad_message: z.nullable(z.object({
      title: z.nullable(z.string()),
      message: z.nullable(z.string()),
    })),
    _created: z.string(),
    status: z.nullable(z.string()),
    instance_ad_id: z.nullable(z.number()),
});

const linkedin_config_schema = z.object({
    end_time: z.nullable(z.string()),
    instance_ad_message: z.nullable(z.object({
        title: z.nullable(z.string()),
        message: z.nullable(z.string()),
    })),
    cost_type: z.nullable(z.string()),
    instance_ad_id: z.nullable(z.number()),
    unit_cost: z.nullable(z.string()),
    _created: z.string(),
    _owner_id: z.number(),
    total_budget: z.nullable(z.number()),
    _deleted: z.nullable(z.string()),
    id: z.number(),
    conversion_tracking_id: z.nullable(z.number()),
    _updated: z.nullable(z.string()),
    _smart_creative_id: z.number(),
    budget: z.nullable(z.number()),
    _creator: z.nullable(z.string()),
    status: z.nullable(z.string()),
    bid_amount: z.nullable(z.number()),
    start_time: z.nullable(z.string()),
    instance_ad_status: z.nullable(z.string()),
});

export const smart_campaign_schema = z.object({
    id: z.number(),
    name: z.string(),
    objective: z.string(),
    audience_id: z.nullable(z.number()),
    start_time: z.string(),
    day_in_week: z.nullable(z.array(z.number())),
    end_minute: z.nullable(z.string()),
    _deleted: z.nullable(z.string()),
    _creator: z.nullable(z.string()),
    _owner_id: z.number(),
    status: z.nullable(z.string()),
    brand: z.object({
      id: z.number(),
      logo: z.nullable(z.unknown()),
      url: z.nullable(z.string()),
      instagram_asset_id: z.nullable(z.number()),
      meta_pixel_id: z.nullable(z.unknown()),
      linkedin_ad_account: z.nullable(z.number()),
      tiktok_identity_id: z.nullable(z.number()),
      tiktok_pixel_id: z.nullable(z.unknown()),
      _owner_id: z.number(),
      name: z.string(),
      meta_page: z.nullable(z.number()),
      ad_account_id: z.nullable(z.number()),
      linkedin_org: z.nullable(z.number()),
      tiktok_user_id: z.nullable(z.number()),
      tiktok_ad_account_id: z.nullable(z.number()),
    }),
    networks: z.array(z.string()),
    end_time: z.nullable(z.string()),
    start_minute: z.nullable(z.string()),
    _created: z.string(),
    _updated: z.nullable(z.string()),
});

export const creative_schema = z.object({
    id: z.number(),
    file_id: z.number(),
    link: z.string(),
    message: z.string(),
    meta__system_status: z.nullable(z.unknown()),
    _created: z.string(),
    _updated: z.nullable(z.unknown()),
    _owner_id: z.number(),
    _smart_campaign_id: z.number(),
    name: z.string(),
    headline: z.string(),
    audience_id: z.nullable(z.unknown()),
    meta__system_message: z.nullable(z.unknown()),
    _deleted: z.nullable(z.unknown()),
    _creator: z.nullable(z.unknown()),
    smart_campaign: smart_campaign_schema,
    linkedin_config: z.nullable(linkedin_config_schema),
    facebook_config: z.nullable(facebook_config_schema),
    tiktok_config: z.nullable(tiktok_config_schema),
});

export type creative_schema_t = z.infer<typeof creative_schema>
export const creatives_schema = z.array(creative_schema)
// ====== end creative =======


// ====== stats ===========
export const overview_stats_schema = z.object({
    result: z.nullable(z.number()),
    impression: z.nullable(z.number()),
    reach: z.nullable(z.number()),
    amount_spend: z.nullable(z.number()),
    end_date: z.nullable(z.string()),
    _owner_id: z.number(),
    relevant_score: z.nullable(z.string()),
    cost_per_result: z.nullable(z.number()),
    frequency: z.nullable(z.number()),
    total_budget: z.nullable(z.number()),
})

export type overview_stats_t = z.infer<typeof overview_stats_schema>
export const overview_stats_return_schema = z.array(overview_stats_schema)

export const creative_stats_schema = z.object({
    reach: z.nullable(z.number()),
    daily_budget: z.nullable(z.number()),
    relevant_score: z.nullable(z.string()),
    extra_data: z.record(z.unknown()).nullable(),
    result: z.nullable(z.number()),
    cost_per_result: z.nullable(z.number()),
    id: z.number(),
    amount_spend: z.nullable(z.number()),
    frequency: z.nullable(z.number()),
    currency: z.string(),
    end_date: z.nullable(z.string()),
    impression: z.nullable(z.number()),
    total_budget: z.nullable(z.number()),
})


export const creative_status_schema = z.object({
    facebook: z.string().nullable(),
    linkedin: z.string().nullable(),
    tiktok: z.string().nullable(),
});


export const creative_network_stats_schema = z.object({
  facebook: z.nullable(creative_stats_schema),
  linkedin: z.nullable(creative_stats_schema),
  tiktok: z.nullable(creative_stats_schema),
});


export const creative_overview_schema = z.object({
  status: creative_status_schema,
  statistics: creative_network_stats_schema,
});

export type creative_overview_t = z.infer<typeof creative_overview_schema>
export type creative_stats_schema_t = z.infer<typeof creative_stats_schema>
// ====== end stats ========

// ====== ad instance schema ====
export const linkedin_campaign_group_schema = z.object({
    backfilled: z.boolean(),
    test: z.boolean(),
    name: z.string(),
    allowed_campaign_types: z.string().nullable(),
    start_time: z.string(),
    created__actor: z.string(),
    _owner_id: z.number(),
    stop_time: z.string().nullable(),
    created__time: z.string(),
    id: z.number(),
    serving_statuses: z.array(z.string()),
    last_modified__actor: z.string(),
    _ad_account_id: z.number(),
    status: z.string(),
    last_modified__time: z.string(),
    linkedin_id: z.string(),
    total_budget__amount: z.string().nullable(),
    extra_data: z.record(z.unknown()).nullable(),
    account: z.string(),
    total_budget__currency_code: z.string().nullable(),
});

export const linkedin_creative_schema = z.object({
    id: z.number(),
    processing_state: z.string().nullable(),
    serving_statuses: z.array(z.string()),
    extra_data: z.record(z.unknown()).nullable(),
    reference: z.string().nullable(),
    created__actor: z.string().nullable(),
    review__review_status: z.string().nullable(),
    created__time: z.string(),
    _owner_id: z.number(),
    review__rejection_reason: z.array(z.string()).nullable(),
    last_modified__actor: z.string().nullable(),
    _campaign_id: z.number(),
    status: z.string(),
    last_modified__time: z.string().nullable(),
    _ad_account_id: z.number(),
    type: z.string().nullable(),
    meta__system_status: z.string().nullable(),
    linkedin_id: z.string(),
    variables: z.string().nullable(),
    meta__system_message: z.string().nullable(),
    campaign: z.string(),
    test: z.boolean(),
});

export const linkedin_ad_instance_schema = z.object({
    campaign_group: linkedin_campaign_group_schema,
    creative: linkedin_creative_schema,
});

export type linkedin_ad_instance_t = z.infer<typeof linkedin_ad_instance_schema>

export const linkedin_preview_schema = z.object({
    src: z.string(),
    height: z.string(),
    width: z.string()
})
export type linkedin_preview_t = z.infer<typeof linkedin_preview_schema>


export const facebook_campaign_schema = z.object({
    id: z.number(),
    _ad_account_id: z.number(),
    objective: z.string(),
    start_time: z.string(),
    facebook_id: z.string(),
    special_ad_categories: z.array(z.unknown()),
    stop_time: z.string().nullable(),
    account_id: z.string(),
    created_time: z.string(),
    updated_time: z.string(),
    ad_strategy_id: z.string().nullable(),
    daily_budget: z.number().nullable(),
    buying_type: z.string(),
    effective_status: z.string(),
    bid_strategy: z.string().nullable(),
    issues_info: z.string().nullable(),
    name: z.string(),
    lifetime_budget: z.number().nullable(),
    status: z.string(),
    source_campaign_id: z.string().nullable(),
});

export const facebook_ad_schema = z.object({
    id: z.number(),
    account_id: z.string(),
    created_time: z.string(),
    adset_id: z.string(),
    preview_shareable_link: z.string().nullable(),
    bid_amount: z.number(),
    source_ad_id: z.string(),
    campaign_id: z.string(),
    updated_time: z.string().nullable(),
    configured_status: z.string(),
    meta__system_status: z.string().nullable(),
    status: z.string(),
    meta__system_message: z.string().nullable(),
    facebook_id: z.string(),
    effective_status: z.string(),
    name: z.string(),
    // https://developers.facebook.com/docs/marketing-api/reference/adgroup-review-feedback/
    ad_review_feedback: z.record(z.unknown()).nullable(),
});

export const facebook_ad_instance_schema = z.object({
    campaign: facebook_campaign_schema,
    ad: facebook_ad_schema,
});

export type facebook_ad_instance_t = z.infer<typeof facebook_ad_instance_schema>

export const facebook_preview_schema = z.object({
    src: z.string(),
    height: z.string()
})
export type facebook_preview_t = z.infer<typeof facebook_preview_schema>

export const tiktok_campaign_schema = z.object({
    advertiser_id: z.string(),
    bid_type: z.string().nullable(),
    budget: z.number(),
    is_new_structure: z.boolean(),
    budget_mode: z.string(),
    create_time: z.string(),
    secondary_status: z.string().nullable(),
    modify_time: z.string(),
    campaign_id: z.string(),
    operation_status: z.string(),
    roas_bid: z.number(),
    campaign_name: z.string(),
    objective: z.string(),
    deep_bid_type: z.string().nullable(),
    campaign_type: z.string(),
    objective_type: z.string(),
    special_industries: z.array(z.string()).nullable(),
    id: z.number(),
    budget_optimize_switch: z.string().nullable(),
    extra_data: z.record(z.unknown()).nullable()
});

export const tiktok_ad_schema = z.object({
    ad_name: z.string(),
    landing_page_url: z.string(),
    playable_url: z.string(),
    id: z.number(),
    call_to_action_id: z.string().nullable(),
    display_name: z.string(),
    meta__system_status: z.string(),
    call_to_action: z.string(),
    impression_tracking_url: z.string().nullable(),
    meta__system_message: z.string().nullable(),
    creative_type: z.string().nullable(),
    create_time: z.string(),
    extra_data: z.record(z.unknown()).nullable(),
    _owner_id: z.number(),
    image_ids: z.array(z.string()),
    modify_time: z.string(),
    ad_format: z.string(),
    click_tracking_url: z.string().nullable(),
    ad_text: z.string(),
    operation_status: z.string(),
    ad_id: z.string(),
    video_id: z.string(),
    secondary_status: z.string().nullable()
});

export const tiktok_ad_instance_schema = z.object({
    campaign: tiktok_campaign_schema,
    ad: tiktok_ad_schema,
});

export type tiktok_ad_instance_t = z.infer<typeof tiktok_ad_instance_schema>

// ======= end ad instance =======

// ======= unified preview API =======
export const preview_creative_schema = z.object({
    headline: z.string(),
    message: z.string(),
    image_url: z.string().optional(),
    image_hash: z.string().optional(),
    link: z.string().optional(),
    facebook_page_id: z.string().optional(),
    instagram_actor_id: z.string().optional(),
    linkedin_organization_id: z.string().optional(),
    tiktok_advertiser_id: z.string().optional()
})

export const preview_request_schema = z.object({
    networks: z.array(z.string()),
    placements: z.record(z.string(), z.array(z.string())),
    creative: preview_creative_schema,
    ad_account_ids: z.record(z.string(), z.string()).optional()
})

export const preview_data_schema = z.object({
    src: z.string().optional(),
    width: z.string().optional(),
    height: z.string().optional(),
    // for tiktok component-based previews
    component_data: z.any().optional(),
    // for x html previews
    html_content: z.string().optional()
})

export const preview_item_schema = z.object({
    network: z.string(),
    placement: z.string(),
    placement_name: z.string(),
    preview_type: z.enum(['iframe', 'component', 'image', 'html']),
    preview_data: preview_data_schema,
    device_type: z.enum(['desktop', 'mobile']).optional(),
    error: z.string().nullable().optional()
})

export const unsupported_placement_schema = z.object({
    network: z.string(),
    placement: z.string(),
    reason: z.string()
})

export const preview_response_schema = z.object({
    previews: z.array(preview_item_schema),
    unsupported_placements: z.array(unsupported_placement_schema).optional().default([]),
    errors: z.array(z.string()).optional().default([]),
    warnings: z.array(z.string()).optional().default([])
})

export type preview_creative_t = z.infer<typeof preview_creative_schema>
export type preview_request_t = z.infer<typeof preview_request_schema>
export type preview_item_t = z.infer<typeof preview_item_schema>
export type unsupported_placement_t = z.infer<typeof unsupported_placement_schema>
export type preview_response_t = z.infer<typeof preview_response_schema>
// ======= end unified preview API =======

// ======= Dashboard API =======
export const dashboard_network_stat_schema = z.object({
    network: z.string(),
    active_campaigns: z.number(),
    total_spend: z.number(),
    conversions: z.number()
})

export const dashboard_connected_brand_schema = z.object({
    brand_id: z.number(),
    brand_name: z.string(),
    has_facebook: z.boolean(),
    has_linkedin: z.boolean(),
    has_tiktok: z.boolean(),
    facebook_ad_account_name: z.string().nullable(),
    linkedin_ad_account_name: z.string().nullable(),
    tiktok_ad_account_name: z.string().nullable()
})

export const dashboard_recent_campaign_schema = z.object({
    id: z.number(),
    name: z.string(),
    status: z.string(),
    objective: z.string(),
    networks: z.array(z.string()),
    total_spend: z.number(),
    total_budget: z.number(),
    impressions: z.number(),
    clicks: z.number(),
    conversions: z.number(),
    start_time: z.string(),
    end_time: z.string().nullable(),
    currency: z.string()
})

export const dashboard_overview_schema = z.object({
    total_active_campaigns: z.number(),
    total_spend_30d: z.number(),
    total_impressions_30d: z.number(),
    total_clicks_30d: z.number(),
    total_conversions_30d: z.number(),
    network_stats: z.array(dashboard_network_stat_schema),
    connected_brands: z.array(dashboard_connected_brand_schema),
    recent_campaigns: z.array(dashboard_recent_campaign_schema),
    monthly_spend: z.number(),
    monthly_limit: z.number(),
    usage_percentage: z.number()
})

export const dashboard_network_health_item_schema = z.object({
    network: z.string(),
    is_connected: z.boolean(),
    ad_account_name: z.string().nullable(),
    ad_account_id: z.string().nullable(),
    token_expires_at: z.string().nullable(),
    needs_reauth: z.boolean(),
    last_sync: z.string().nullable()
})

export const dashboard_network_health_schema = z.array(dashboard_network_health_item_schema)

export const dashboard_top_performing_campaign_schema = z.object({
    campaign_id: z.number(),
    campaign_name: z.string(),
    network: z.string(),
    metric_value: z.number(),
    metric_name: z.string()
})

export const dashboard_top_performing_schema = z.array(dashboard_top_performing_campaign_schema)

export type dashboard_network_stat_t = z.infer<typeof dashboard_network_stat_schema>
export type dashboard_connected_brand_t = z.infer<typeof dashboard_connected_brand_schema>
export type dashboard_recent_campaign_t = z.infer<typeof dashboard_recent_campaign_schema>
export type dashboard_overview_t = z.infer<typeof dashboard_overview_schema>
export type dashboard_network_health_item_t = z.infer<typeof dashboard_network_health_item_schema>
export type dashboard_network_health_t = z.infer<typeof dashboard_network_health_schema>
export type dashboard_top_performing_campaign_t = z.infer<typeof dashboard_top_performing_campaign_schema>
export type dashboard_top_performing_t = z.infer<typeof dashboard_top_performing_schema>
// ======= end Dashboard API =======

// ====== Billing and Subscription API =======
export const tier_features_schema = z.object({
    white_label: z.boolean().optional(),
    automation: z.boolean().optional(),
    bulk_operations: z.boolean().optional(),
    unlimited_seats: z.boolean().optional(),
    custom_support: z.boolean().optional(),
})

export const usage_tier_schema = z.object({
    id: z.number(),
    name: z.string(),
    description: z.string(),
    price: z.number(),
    eligibility_duration: z.number(),
    stripe_price_id: z.string(),
    brand_limit: z.number().nullable(),
    campaign_limit: z.number().nullable(),
    team_seats: z.number(),
    features: tier_features_schema,
    usage_limit: z.number(),
})

export const subscription_info_schema = z.object({
    id: z.number(),
    status: z.string(),
    subscription_date: z.string(),
    end_date: z.string(),
    is_paid: z.boolean(),
}).nullable()

export const user_subscription_schema = z.object({
    subscription: subscription_info_schema,
    plan: usage_tier_schema,
    checkout_session_id: z.string().nullable(),
})

export const all_plans_schema = z.array(usage_tier_schema)

export const campaign_eligibility_schema = z.object({
    is_eligible: z.boolean(),
    reason: z.string().optional(),
    current_campaigns: z.number().optional(),
    max_campaigns: z.number().optional(),
})

export type tier_features_t = z.infer<typeof tier_features_schema>
export type usage_tier_t = z.infer<typeof usage_tier_schema>
export type subscription_info_t = z.infer<typeof subscription_info_schema>
export type user_subscription_t = z.infer<typeof user_subscription_schema>
export type campaign_eligibility_t = z.infer<typeof campaign_eligibility_schema>
// ====== end Billing and Subscription API =======
